import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetHeatActivityList } from '@shared/api/hooks/ccf/activities/heating';
import Text from '@shared/components/content/text';
import { useCCF } from '../../ccf-context';
import HeatingEditForm from '../../components/scope-2/heating-edit-form';
import HeatingTable from '../../components/scope-2/heating-table';
import { reportSlugs } from '../../slugs';
import { useCategory } from '../../utils';
import EmptyCategoryPage from '../ccf-category-intro-page';

export default function HeatingPage() {
  const { uuid: reportUuid } = useCCF();
  const category = useCategory(reportSlugs.heat);
  const {
    showSimpleDialog: showHeatingDialog,
    SimpleDialogComponent: HeatingDialog,
    closeSimpleDialog: closeHeatingDialog,
  } = useSimpleDialog();

  const { page, perPage, period, site, sort, supplier } =
    useDataTableQueryState({
      filters: ['supplier', 'period', 'site'],
    });

  const {
    data,
    isPreviousData,
    isFetchedAfterMount,
    isLoading,
    isError,
    isFetching,
    status,
  } = useGetHeatActivityList(reportUuid!, {
    page,
    page_size: perPage,
    suppliers: supplier || undefined,
    time_periods: period || undefined,
    sites: site || undefined,
    sort: formatMultipleSortingForEndpoint(sort! || []),
  });

  const description = (
    <>
      <Text>
        Heating and steam refers to the{' '}
        <span className="font-semibold">energy your business purchases</span>{' '}
        externally to power operations, heat buildings, or support processes
        like sterilisation and production. These energy purchases result in
        indirect emissions (Scope 2) generated during the production of heating
        and steam.
      </Text>
      <Text className="mt-3">Examples include:</Text>
      <Text>
        <ul className="list-disc pl-5">
          <li>
            <span className="font-semibold">Heat generated</span> at a central
            plant and distributed via a network to your facilities.
          </li>
          <li>
            <span className="font-semibold">Steam purchased</span> for
            industrial or operational uses, such as cleaning, sterilisation, or
            production.
          </li>
        </ul>
      </Text>
    </>
  );

  return (
    <Page name="Heating & Steam" page="subpage">
      {category.hasActivityData ? (
        <HeatingTable
          data={data!}
          status={status}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          isPreviousData={isPreviousData}
          isFetchedAfterMount={isFetchedAfterMount}
        />
      ) : (
        <EmptyCategoryPage
          categorySlug="heat"
          description={description}
          primaryActionProps={{
            onClick: showHeatingDialog,
          }}
        />
      )}
      {HeatingDialog({
        header: 'Add heating and steam data',
        content: <HeatingEditForm closeDialog={closeHeatingDialog} />,
      })}
    </Page>
  );
}
